import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { themeColors } from '../theme';
import JVChartPanel, { OVERLAY_COLORS } from './JVChartPanel';
import { WorkspaceContextSetter } from '../context/WorkspaceContext';

const API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1`;

const fmt = (v, d = 3) => v != null ? Number(v).toFixed(d) : '—';

// ── Avarodha Recombination Analysis (moved from ResultsPanel) ───────────────
// This tab owns the Avarodha section + J0 what-if editor so the simulation
// results view stays uncluttered. The light JV chart and the compact metric
// readout are MIRRORED at the top (same `results` object — no extra API
// calls) so the performance impact of J0 changes is visible in one place.
const _J0_CHANNELS = [
  ['J0_radiative',  'J₀ radiative'],
  ['J0_bulk_SRH',   'J₀ bulk SRH'],
  ['J0_GB',         'J₀ grain boundary'],
  ['J0_surface',    'J₀ surface'],
  ['J0_Auger',      'J₀ Auger'],
  ['J0_contact',    'J₀ contact'],
];

function _dominantColor(channelKey) {
  if (channelKey === 'J0_GB')        return '#e85d75';  // red
  if (channelKey === 'J0_surface')   return '#f0a500';  // amber
  if (channelKey === 'J0_radiative') return '#39d353';  // green
  return '#8b949e';                                      // neutral gray
}

// ── Avarodha J0 what-if overlays (2026-07) ──────────────────────────────────
// Editable named overlays of analytic diode channels re-submitted with the
// last /simulation/run payload as `j0_overlays`. The backend adds
// J0·expm1(V/(n·Vth)) per channel on top of the simulated JV and returns
// j0_overlay_results — an ANALYSIS layer (decomposition_method
// "analytic_overlay"), not re-solved transport.
const OVERLAY_CHANNEL_OPTIONS = [
  'J0_radiative', 'J0_bulk_SRH', 'J0_interface',
  'J0_GB', 'J0_Auger', 'J0_contact',
];

const _rid = () => Math.random().toString(36).slice(2);

// Seed a new overlay's channel rows from the simulated device's own J0
// breakdown (avarodha_outputs) when available. J0_surface maps onto the
// overlay channel name J0_interface. Falls back to one GB row.
function _seedOverlayChannels(outputs) {
  const seedMap = [
    ['J0_radiative', outputs?.J0_radiative],
    ['J0_bulk_SRH',  outputs?.J0_bulk_SRH],
    ['J0_interface', outputs?.J0_surface],
    ['J0_GB',        outputs?.J0_GB],
    ['J0_Auger',     outputs?.J0_Auger],
    ['J0_contact',   outputs?.J0_contact],
  ];
  const rows = seedMap
    .filter(([, v]) => typeof v === 'number' && v > 0)
    .map(([name, v]) => ({ id: _rid(), name, j0Text: v.toExponential(2), nText: '1.0' }));
  if (rows.length > 0) return rows;
  return [{ id: _rid(), name: 'J0_GB', j0Text: '1e-11', nText: '2.0' }];
}

// Overlay Δ table — rendered in the right column below the what-if
// editor (extracted from J0WhatIfEditor so the deltas sit next to the
// Avarodha parameters while the curves render on the left chart).
function OverlayDeltaTable({ overlayResults, c }) {
  if (!overlayResults || overlayResults.length === 0) return null;
  return (
    <>
      <table style={{
        width: '100%', marginTop: 8, borderCollapse: 'collapse',
        fontSize: 11, fontFamily: 'IBM Plex Mono, monospace',
      }}>
        <thead>
          <tr style={{ color: c.textMuted }}>
            {['overlay', 'Voc (V)', 'FF (%)', 'PCE (%)', 'ΔVoc (mV)', 'ΔFF (pts)'].map(h => (
              <th key={h} style={{
                textAlign: h === 'overlay' ? 'left' : 'right',
                padding: '2px 4px', borderBottom: `1px solid ${c.border}`,
                fontWeight: 400,
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {overlayResults.map((ov, i) => {
            const m = ov.metrics || {};
            const d = ov.delta_vs_base || {};
            const cell = { padding: '2px 4px', textAlign: 'right', color: c.text };
            return (
              <tr key={i}>
                <td style={{ ...cell, textAlign: 'left',
                             color: OVERLAY_COLORS[i % OVERLAY_COLORS.length] }}>
                  {ov.label}
                </td>
                <td style={cell}>{m.Voc != null ? Number(m.Voc).toFixed(3) : '—'}</td>
                <td style={cell}>{m.FF != null ? Number(m.FF).toFixed(1) : '—'}</td>
                <td style={cell}>{m.PCE != null ? Number(m.PCE).toFixed(2) : '—'}</td>
                <td style={cell}>{d.Voc != null ? (d.Voc * 1000).toFixed(0) : '—'}</td>
                <td style={cell}>{d.FF != null ? Number(d.FF).toFixed(1) : '—'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ fontSize: 10, color: c.textMuted, marginTop: 4, fontStyle: 'italic' }}>
        Analytic diode overlays on the simulated curve — not re-solved
        transport.
      </div>
    </>
  );
}

function J0WhatIfEditor({ outputs, canRun, running, error, onRun, c }) {
  const [overlays, setOverlays] = React.useState([]);

  const addOverlay = () => setOverlays(os => [...os, {
    id: _rid(),
    label: `Overlay ${os.length + 1}`,
    channels: _seedOverlayChannels(outputs),
  }]);
  const removeOverlay = (id) => setOverlays(os => os.filter(o => o.id !== id));
  const updOverlay = (id, patch) =>
    setOverlays(os => os.map(o => o.id === id ? { ...o, ...patch } : o));
  const addChannel = (oid) => setOverlays(os => os.map(o => o.id === oid
    ? { ...o, channels: [...o.channels, { id: _rid(), name: 'J0_GB', j0Text: '1e-11', nText: '2.0' }] }
    : o));
  const removeChannel = (oid, cid) => setOverlays(os => os.map(o => o.id === oid
    ? { ...o, channels: o.channels.filter(ch => ch.id !== cid) }
    : o));
  const updChannel = (oid, cid, patch) => setOverlays(os => os.map(o => o.id === oid
    ? { ...o, channels: o.channels.map(ch => ch.id === cid ? { ...ch, ...patch } : ch) }
    : o));

  // Serialize to the API j0_overlays shape, dropping non-numeric rows.
  const payload = overlays.map(o => ({
    label: (o.label || '').trim() || 'overlay',
    channels: o.channels
      .map(ch => ({ name: ch.name, J0_mA_cm2: parseFloat(ch.j0Text), n: parseFloat(ch.nText) }))
      .filter(ch => Number.isFinite(ch.J0_mA_cm2) && ch.J0_mA_cm2 > 0
                 && Number.isFinite(ch.n) && ch.n > 0),
  })).filter(o => o.channels.length > 0);

  const inp = {
    background: c.input, border: `1px solid ${c.border}`, borderRadius: 3,
    color: c.inputText, fontFamily: 'IBM Plex Mono, monospace', fontSize: 11,
    padding: '2px 5px',
  };
  const miniBtn = {
    background: 'none', border: `1px dashed ${c.border}`, borderRadius: 3,
    color: c.textMuted, cursor: 'pointer', fontSize: 10, padding: '1px 7px',
    fontFamily: 'IBM Plex Mono, monospace',
  };

  return (
    <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px dashed ${c.border}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ color: c.textMuted, fontSize: 11, letterSpacing: 0.5 }}>
          WHAT-IF J₀ OVERLAYS
        </span>
        <button type="button" onClick={addOverlay} style={miniBtn}>+ Add overlay</button>
      </div>

      {overlays.length === 0 && (
        <div style={{ fontSize: 11, color: c.textMuted, fontStyle: 'italic' }}>
          Add a named overlay of analytic J₀ channels (seeded from this
          device's own breakdown when available) and re-run to see the
          Voc/FF/PCE impact.
        </div>
      )}

      {overlays.map(ov => (
        <div key={ov.id} style={{
          border: `1px solid ${c.border}`, borderRadius: 4,
          padding: '6px 8px', marginBottom: 6,
        }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
            <input style={{ ...inp, flex: 1 }} type="text" value={ov.label}
              placeholder="overlay label"
              onChange={e => updOverlay(ov.id, { label: e.target.value })} />
            <button type="button" onClick={() => addChannel(ov.id)} style={miniBtn}>+ channel</button>
            <button type="button" onClick={() => removeOverlay(ov.id)}
              title="Remove overlay"
              style={{ background: 'none', border: 'none', color: c.red ?? '#ff4757',
                       cursor: 'pointer', fontSize: 13, padding: '0 2px' }}>&times;</button>
          </div>
          {ov.channels.map(ch => (
            <div key={ch.id} style={{
              display: 'grid', gridTemplateColumns: '1fr 90px 46px 18px',
              gap: 6, alignItems: 'center', marginBottom: 3,
            }}>
              <select style={inp} value={ch.name}
                onChange={e => updChannel(ov.id, ch.id, { name: e.target.value })}>
                {OVERLAY_CHANNEL_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
              <input style={inp} type="text" value={ch.j0Text}
                title="J0 in mA/cm² (scientific notation OK, e.g. 2.5e-10)"
                placeholder="J0 mA/cm²"
                onChange={e => updChannel(ov.id, ch.id, { j0Text: e.target.value })} />
              <input style={inp} type="text" value={ch.nText}
                title="Ideality factor n — 1.0 typical; 2.0 typical for GB / depletion channels"
                placeholder="n"
                onChange={e => updChannel(ov.id, ch.id, { nText: e.target.value })} />
              <button type="button" onClick={() => removeChannel(ov.id, ch.id)}
                title="Remove channel"
                style={{ background: 'none', border: 'none', color: c.red ?? '#ff4757',
                         cursor: 'pointer', fontSize: 12, padding: 0 }}>&times;</button>
            </div>
          ))}
        </div>
      ))}

      {overlays.length > 0 && (
        <div style={{ fontSize: 10, color: c.textMuted, marginBottom: 6 }}>
          J₀ in mA/cm² · ideality n defaults to 1.0 (2.0 is typical for
          GB / depletion channels).
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button type="button"
          onClick={() => onRun(payload)}
          disabled={!canRun || running || payload.length === 0}
          title={!canRun
            ? 'No stored run payload — run a simulation first'
            : 'Re-submit the last simulation request with these j0_overlays attached'}
          style={{
            background: (!canRun || running || payload.length === 0) ? c.btnSecondary : c.accent + '22',
            color: (!canRun || running || payload.length === 0) ? c.textMuted : c.accent,
            border: `1px solid ${(!canRun || running || payload.length === 0) ? c.border : c.accent}`,
            borderRadius: 4, padding: '4px 12px',
            cursor: (!canRun || running || payload.length === 0) ? 'not-allowed' : 'pointer',
            fontFamily: 'IBM Plex Mono, monospace', fontSize: 11,
          }}>
          {running ? '⟳ Running…' : '▶ Run with overlays'}
        </button>
        {error && <span style={{ color: c.red ?? '#ff4757', fontSize: 11 }}>{error}</span>}
      </div>
    </div>
  );
}

function AvarodhaAnalysis({ outputs, currentVoc, c, whatif }) {
  const [open, setOpen] = React.useState(true);

  const header = (
    <div
      onClick={() => setOpen(o => !o)}
      style={{
        marginTop: 12, padding: '8px 12px',
        background: c.bgSecondary, border: `1px solid ${c.border}`,
        borderRadius: 6, cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        fontFamily: 'IBM Plex Mono, monospace', fontSize: 12,
        color: c.textMuted, letterSpacing: 0.5,
      }}
    >
      <span>{open ? '▼' : '▶'} AVARODHA RECOMBINATION ANALYSIS</span>
      {!outputs && <span style={{ color: c.textMuted, fontSize: 11 }}>not available</span>}
    </div>
  );

  if (!open) return header;

  // No-data path: solver hasn't computed avarodha_outputs yet.
  if (!outputs) {
    return (
      <>
        {header}
        <div style={{
          padding: '12px 14px', marginTop: -1,
          background: c.bgSecondary,
          border: `1px solid ${c.border}`, borderTop: 'none',
          borderRadius: '0 0 6px 6px',
          color: c.textMuted, fontSize: 12, fontStyle: 'italic',
        }}>
          Avarodha analysis not yet available for this device configuration.
          (The solver's <code>_compute_avarodha_outputs</code> implementation
          is planned but not yet shipped. This panel will populate
          automatically when the API returns <code>avarodha_outputs</code>.)
          {whatif}
        </div>
      </>
    );
  }

  // Compute total J0 (sum of present channels) for percentage breakdown.
  const channelVals = _J0_CHANNELS.map(([k, label]) => ({
    key: k,
    label,
    val: typeof outputs[k] === 'number' ? outputs[k] : null,
  }));
  const totalJ0 = channelVals.reduce((s, ch) => s + (ch.val || 0), 0);
  const dominant = channelVals
    .filter(ch => ch.val != null)
    .reduce((best, ch) => (best == null || ch.val > best.val ? ch : best), null);
  const dominantPct = (dominant && totalJ0 > 0)
    ? (dominant.val / totalJ0) * 100 : null;
  const dominantColor = dominant ? _dominantColor(dominant.key) : c.textMuted;

  const ceiling = outputs.voc_ceiling;
  const dmu = outputs.delta_mu_star;
  const feff = outputs.feff;
  const vocPctOfCeiling = (ceiling != null && currentVoc != null && ceiling > 0)
    ? (currentVoc / ceiling) * 100 : null;
  const nearCeiling = (ceiling != null && currentVoc != null
                       && (ceiling - currentVoc) <= 0.05
                       && (ceiling - currentVoc) >= 0);

  const fmtJ0 = v => v == null ? '—' : v.toExponential(2);
  const fmtPct = p => p == null ? '—' : p.toFixed(1) + '%';
  const fmtV   = v => v == null ? '—' : v.toFixed(3);

  return (
    <>
      {header}
      <div style={{
        padding: '12px 14px', marginTop: -1,
        background: c.bgSecondary,
        border: `1px solid ${c.border}`, borderTop: 'none',
        borderRadius: '0 0 6px 6px',
        color: c.text, fontFamily: 'IBM Plex Mono, monospace', fontSize: 12,
      }}>
        {/* Ceiling summary */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
          <div>
            <div style={{ color: c.textMuted, fontSize: 11 }}>Voc ceiling (Δμ*/q)</div>
            <div style={{ fontSize: 14, color: c.text }}>
              {fmtV(ceiling)} V
              {dmu != null && (
                <span style={{ color: c.textMuted, fontSize: 11, marginLeft: 6 }}>
                  (Δμ* = {dmu.toExponential(2)} eV)
                </span>
              )}
            </div>
          </div>
          <div>
            <div style={{ color: c.textMuted, fontSize: 11 }}>Current Voc</div>
            <div style={{ fontSize: 14, color: nearCeiling ? '#fb8500' : c.text }}>
              {fmtV(currentVoc)} V
              {vocPctOfCeiling != null && (
                <span style={{ color: c.textMuted, fontSize: 11, marginLeft: 6 }}>
                  ({vocPctOfCeiling.toFixed(0)}% of ceiling)
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Per-channel J0 breakdown — table + horizontal bar chart in one row */}
        <div style={{ color: c.textMuted, fontSize: 11, marginBottom: 4 }}>
          Per-channel J₀ breakdown:
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {channelVals.map(ch => {
            const pct = (ch.val != null && totalJ0 > 0) ? (ch.val / totalJ0) * 100 : null;
            const color = (dominant && ch.key === dominant.key) ? dominantColor : c.border;
            return (
              <div key={ch.key} style={{ display: 'grid', gridTemplateColumns: '110px 100px 1fr 50px', gap: 8, alignItems: 'center' }}>
                <div style={{ fontSize: 11, color: c.textMuted }}>{ch.label}</div>
                <div style={{ fontSize: 11, color: c.text, textAlign: 'right' }}>
                  {fmtJ0(ch.val)}
                </div>
                <div style={{ position: 'relative', height: 10, background: 'rgba(255,255,255,0.04)', borderRadius: 2 }}>
                  {pct != null && (
                    <div style={{
                      height: '100%', width: `${Math.min(pct, 100)}%`,
                      background: color, borderRadius: 2, transition: 'width 0.2s',
                    }} />
                  )}
                </div>
                <div style={{ fontSize: 11, color: c.textMuted, textAlign: 'right' }}>
                  {fmtPct(pct)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Dominant channel + ceiling status */}
        <div style={{
          marginTop: 12, paddingTop: 10, borderTop: `1px dashed ${c.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <span style={{ color: c.textMuted, fontSize: 11 }}>Dominant channel: </span>
            {dominant ? (
              <span style={{ color: dominantColor, fontSize: 12, fontWeight: 600 }}>
                {dominant.label} ({fmtPct(dominantPct)})
              </span>
            ) : <span style={{ color: c.textMuted }}>—</span>}
          </div>
          <div>
            <span style={{ color: c.textMuted, fontSize: 11 }}>Ceiling: </span>
            <span style={{
              color: nearCeiling ? '#fb8500' : '#39d353',
              fontSize: 12, fontWeight: 600,
            }}>
              {nearCeiling ? 'NEAR CEILING' : 'BELOW'}
            </span>
          </div>
        </div>
        {feff != null && (
          <div style={{ marginTop: 6, fontSize: 11, color: c.textMuted }}>
            Participation fraction f_eff: {feff.toExponential(2)}
          </div>
        )}
        {whatif}
      </div>
    </>
  );
}

export default function AvarodhaPanel({ results, theme = 'dark' }) {
  const c = themeColors(theme);

  // ── J0 what-if overlay results ────────────────────────────────────
  // Populated by re-posting the last run payload (results._lastPayload,
  // stored by SimulatorPanel.handleRun) with j0_overlays attached.
  // Cleared whenever a new base simulation arrives.
  const [overlayResults, setOverlayResults] = useState(null);
  const [overlayRunning, setOverlayRunning] = useState(false);
  const [overlayError, setOverlayError] = useState(null);
  useEffect(() => {
    setOverlayResults(null);
    setOverlayError(null);
  }, [results]);

  // ── Responsive two-column layout (2026-07) ────────────────────────
  // Chart + metrics on the left, Avarodha parameters + J0 what-if on
  // the right; stacks back to a single column below 1100 px (same
  // window-width-state pattern as PersistentAI's viewport checks).
  const [narrow, setNarrow] = useState(() => window.innerWidth < 1100);
  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 1100);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const m = results?.metrics || {};
  const outputs = results?.avarodha_outputs || null;

  // ── Publish to WorkspaceContext ('avarodha' slice) ────────────────
  // Follows the per-tab publishing pattern: the AI chat sees the current
  // J0 breakdown plus any what-if overlay results.
  const publish = useContext(WorkspaceContextSetter);
  useEffect(() => {
    const j0 = outputs
      ? Object.fromEntries(_J0_CHANNELS
          .filter(([k]) => typeof outputs[k] === 'number')
          .map(([k]) => [k, outputs[k]]))
      : null;
    publish('avarodha', {
      has_results: !!results,
      current_Voc: m.Voc ?? null,
      voc_ceiling: outputs?.voc_ceiling ?? null,
      j0_channels: j0,
      overlay_running: overlayRunning,
      overlay_results: (overlayResults || []).map(ov => ({
        label: ov.label,
        metrics: ov.metrics || null,
        delta_vs_base: ov.delta_vs_base || null,
      })),
    });
  }, [publish, results, outputs, m.Voc, overlayRunning, overlayResults]);

  if (!results) {
    return (
      <div className="results-panel">
        <div className="empty-state">
          <div className="empty-icon">◈</div>
          <div className="empty-title">No Results</div>
          <div className="empty-sub">
            Run a simulation to see the Avarodha recombination analysis,
            the per-channel J₀ breakdown, and the J₀ what-if editor.
          </div>
        </div>
      </div>
    );
  }

  const jvLight = results.jv_curve || [];
  const jvDark = results.jv_dark || [];
  const Jsc_display = m.Jsc != null ? Math.abs(m.Jsc).toFixed(2) : '—';

  async function runOverlays(j0_overlays) {
    const base = results._lastPayload;
    if (!base || overlayRunning) return;
    setOverlayRunning(true);
    setOverlayError(null);
    try {
      const { data } = await axios.post(`${API}/simulation/run`,
        { ...base, j0_overlays });
      if (data && data.success === false) {
        setOverlayError(data.error || 'Overlay run failed');
        return;
      }
      setOverlayResults(data?.j0_overlay_results || []);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setOverlayError(typeof detail === 'string'
        ? detail : (err.message || 'Overlay run failed'));
    } finally {
      setOverlayRunning(false);
    }
  }

  return (
    <div className="results-panel" id="avarodha-results">

      {/* ── Two-column layout: simulated JV readout on the LEFT,
             Avarodha parameters + J0 what-if on the RIGHT. Stacks to a
             single column below 1100 px (`narrow`). ───────────────── */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: narrow ? '1fr' : '1fr 1fr',
        gap: 16, alignItems: 'start',
        paddingBottom: 24,
      }}>

        {/* ── LEFT: compact Jsc/Voc/FF/PCE metrics bar + mirrored light
               JV chart (same results object as the simulation tab — no
               extra API calls; overlay curves render here too). ───── */}
        <div style={{ minWidth: 0 }}>
          <div className="metrics-bar">
            <div className="metric-cell">
              <div className="metric-name">V<sub>oc</sub></div>
              <div className="metric-value highlight">{fmt(m.Voc, 4)}<span className="metric-unit">V</span></div>
            </div>
            <div className="metric-cell">
              <div className="metric-name">J<sub>sc</sub></div>
              <div className="metric-value">{Jsc_display}<span className="metric-unit">mA/cm²</span></div>
            </div>
            <div className="metric-cell">
              <div className="metric-name">FF</div>
              <div className="metric-value">{fmt(m.FF, 1)}<span className="metric-unit">%</span></div>
            </div>
            <div className="metric-cell">
              <div className="metric-name">PCE</div>
              <div className="metric-value highlight">{fmt(m.PCE, 2)}<span className="metric-unit">%</span></div>
            </div>
          </div>

          <div className="charts-area" style={{ gridTemplateColumns: '1fr', flex: '0 0 auto' }}>
            <JVChartPanel
              jvLight={jvLight}
              jvDark={jvDark}
              jvPhoto={results?.jv_photocurrent || null}
              collection={results?.collection_summary || null}
              metrics={m}
              overlayResults={overlayResults}
              theme={theme}
              chartId="avarodha-jv-chart-container"
            />
          </div>
        </div>

        {/* ── RIGHT: Avarodha analysis (per-channel breakdown + ceiling
               summary) + J0 what-if editor, then the overlay Δ table
               once overlays ran. ────────────────────────────────────── */}
        <div style={{ minWidth: 0, padding: narrow ? '0 20px' : '0 20px 0 0' }}>
          <AvarodhaAnalysis
            outputs={outputs}
            currentVoc={m.Voc}
            c={c}
            whatif={
              <J0WhatIfEditor
                outputs={outputs}
                canRun={!!results._lastPayload}
                running={overlayRunning}
                error={overlayError}
                onRun={runOverlays}
                c={c}
              />
            }
          />
          {overlayResults && overlayResults.length > 0 && (
            <OverlayDeltaTable overlayResults={overlayResults} c={c} />
          )}
        </div>
      </div>
    </div>
  );
}
